// Helpers puros de auto-servicio (pedidos de mesa) — endurecen el panel contra la consistencia
// eventual del backend. El `newOrder` (WS) se emite ANTES de que exista la asociación de mesa
// (`OrderItem`); desde que el read se migró a AWS el panel lee el detalle antes de que la
// asociación exista → `self_service:null` → tarjeta suelta que nunca se re-enriquece. Estos
// predicados deciden, sin React ni red, si un pedido es candidato a mesa y si ya llegó a su
// estado agrupado (enriquecido).
//
// Señal de detección (corrección de Fable): `metodo_entrega === 'inRestaurant'`, NO `table_uuid`
// ni `is_self_service` (no existen en el pedido antes del enriquecimiento; `table_uuid` vive
// anidado dentro de `self_service`, que es justamente el objeto ausente). El gate del módulo
// `SELF_SERVICE` se aplica en el call-site (ver `shouldHealSelfService`).

import { Order } from '@interfaces/OrderTypes';

// Payload de la primitiva de merge (`mergeSelfService`) compartido por ambos slices/acciones.
// `self_service` NUNCA es null aquí: solo se mergea cuando el read ya trae `order_uuid`.
export type SelfServiceMergePayload = {
  id: string;
  self_service: NonNullable<Order['self_service']>;
};

// ¿El pedido es candidato a agruparse en una mesa? Verdadero solo para consumo en restaurante.
// Cubre ambos flujos con `OrderItem` (QR self-pay `inRestaurant/web` y handheld `inRestaurant/pos`).
export const isSelfServiceCandidate = (pedido: Pick<Order, 'metodo_entrega'>): boolean =>
  pedido?.metodo_entrega === 'inRestaurant';

// ¿El pedido ya alcanzó su estado agrupado? Verdadero solo si el read trajo la asociación de mesa
// (`self_service.order_uuid`). Es la señal de "ya enriquecido" que corta el retry y protege la
// inmutabilidad del merge (un pedido = un `orders_order`; `self_service` no cambia una vez asignado).
export const isEnrichedSelfService = (pedido: Pick<Order, 'self_service'>): boolean =>
  Boolean(pedido?.self_service?.order_uuid);

// Gate de decisión del call-site (T3): agenda el retry de sanado solo si el pedido es candidato a
// mesa, aún NO está enriquecido, y el comercio tiene el módulo SELF_SERVICE activo. Extraído como
// helper puro para poder testear las combinaciones sin montar `useIncomingOrder`.
export const shouldHealSelfService = (
  pedido: Pick<Order, 'metodo_entrega' | 'self_service'>,
  hasSelfServiceModule: boolean | null,
): boolean => isSelfServiceCandidate(pedido) && !isEnrichedSelfService(pedido) && Boolean(hasSelfServiceModule);
